import type { IndexUiState, UiState } from "instantsearch.js";
import { InstantSearchOptions } from "instantsearch.js/es/lib/InstantSearch";
import { history } from "instantsearch.js/es/lib/routers";
import React from "react";
import { Configure, InstantSearch, Pagination } from "react-instantsearch";
import { SearchBox } from "components/Search/SearchBox";
import { SearchResults } from "components/Search/SearchResults";
import Button from "components/common/Button";
import Container from "components/common/Container";
import { getAlgoliaSearch } from "helpers/algolia";
import { useRouterQuery } from "hooks";
import styles from "./Search.module.css";

const algoliaSearch = getAlgoliaSearch();

const HITS_PER_PAGE = 10;

const Search = () => {
  const { routerQuery } = useRouterQuery();
  const indexName = algoliaSearch?.indexName || "";

  const initialSearchState = {
    [indexName]: {
      query: routerQuery.query || "",
      hitsPerPage: HITS_PER_PAGE,
    },
  };

  const routing: InstantSearchOptions<UiState, IndexUiState>["routing"] = {
    router: history(),
    stateMapping: {
      stateToRoute(uiState) {
        const indexUiState = uiState[indexName];
        return {
          query: indexUiState.query,
          page: indexUiState.page,
        };
      },
      routeToState(routeState) {
        return {
          [indexName]: {
            query: routeState.query,
            page: routeState.page,
          },
        };
      },
    },
  };

  const reloadPage = () => window.location.reload();

  return algoliaSearch ? (
    <InstantSearch
      searchClient={algoliaSearch.searchClient}
      indexName={algoliaSearch.indexName}
      initialUiState={initialSearchState}
      routing={routing}
    >
      <Configure hitsPerPage={HITS_PER_PAGE} distinct />
      <div className={styles.resultsHeader}>
        <SearchBox />
      </div>
      <Container>
        <SearchResults />
        <Pagination
          showFirst={false}
          className="flex justify-center flex-wrap"
          translations={{
            previousPageItemText: "‹ Previous page",
            nextPageItemText: "Next page ›",
          }}
        />
      </Container>
    </InstantSearch>
  ) : (
    <Container>
      <div className={styles.noSearchBox}>
        <h2 className={styles.noSearchTitle}>Search not configured</h2>
        <p className={styles.noSearchInfo}>
          Please try reloading the page. Contact support if the issue persists.
          <Button theme="textButton" className={styles.noSearchReloadButton} onClick={reloadPage}>
            Reload
          </Button>
        </p>
      </div>
    </Container>
  );
};

export default Search;
